import { Material, Object3D } from "three";
import { CSM } from "three/examples/jsm/csm/CSM.js";

import { Context } from "../../poser/context";
import { Player, teamIdToAbbrev } from "../types/statsApi";

import {
  MaterialName,
  MaterialTemplate,
  SkinMaterial,
} from "./material/material";
import { Outfit, OutfitHandedness, OutfitMetadata } from "./outfit";
import { ActorDetails } from "../../poser/world/actor/actor";
import { PoserPlayerType } from "../misc/player-type";

/**
 * Wrapper class for player appearance - currently unused, but
 * could be used when we move to using InstancedMesh for the
 * Actor models
 */
export class Skin {
  outfit: Outfit;
  material: SkinMaterial;

  private lod: number;

  private constructor(outfit: Outfit, material: SkinMaterial, lod = 0) {
    this.outfit = outfit;
    this.material = material;
    this.lod = lod;
  }

  apply(targets: Object3D[], temp = false) {
    this.outfit?.apply(targets, temp, this.lod);
    this.material?.apply(targets);
  }

  static make(context: Context, actorDetails: ActorDetails): Skin | undefined {
    const { teamId, outfitTemplate, materialTemplate } = playerDetails(
      context,
      actorDetails
    );

    if (!outfitTemplate || !materialTemplate) {
      console.error(`no team or outfit for ${actorDetails.uid} found`);
      return undefined;
    }

    if (!context.options.assetBase) {
      console.error("loading materials without asset base");
      return undefined;
    }

    const { bufferManager, gameState, userOptions, world } = context;

    const processMaterials = (m: Material) => {
      if (!m.userData?.csmSetup && world?.lights.sky?.sun instanceof CSM) {
        world.lights.sky.sun.setupMaterial(m);
        m.userData.csmSetup = true;
      }
    };

    let handedness = (
      actorDetails.type == "pitcher"
        ? gameState.getPitchHand()
        : actorDetails.type == "batter"
        ? gameState.getBatHand()
        : actorDetails.player?.person.pitchHand.description
    ) as OutfitHandedness | undefined;
    if (
      !(
        handedness == undefined ||
        handedness == "Left" ||
        handedness == "Right"
      )
    ) {
      console.error("invalid handed code:", handedness);
      handedness = undefined;
    }

    // load statsapi variant uniforms
    let materialVariants: Map<MaterialName, string> | undefined = teamId
      ? bufferManager.uniforms?.variants[teamId]
      : undefined;

    const outfit = Outfit.fromJSON(
      userOptions.hasShadows(),
      outfitTemplate,
      handedness,
      processMaterials
    );
    const material = SkinMaterial.fromJSON(
      materialTemplate,
      context.userOptions.getMaterialCtor().player,
      context.renderer,
      context.options.assetBase,
      materialVariants
    );
    if (!material) {
      console.error("invalid material");
      return undefined;
    }

    return new Skin(outfit, material, userOptions.values.playerLOD);
  }
}

function playerDetails(
  context: Context,
  actorDetails: ActorDetails
): {
  teamId?: number;
  outfitTemplate?: OutfitMetadata;
  materialTemplate?: MaterialTemplate;
} {
  const battingTeam = context.gameState.getBattingTeamId();
  const fieldingTeam = context.gameState.getFieldingTeamId();

  const { player, type } = actorDetails;

  let team: string | undefined = undefined;
  let teamId: number | undefined = undefined;

  if (type == PoserPlayerType.UMPIRE || type == PoserPlayerType.PLATE_UMPIRE) {
    team = "umpire";
  } else {
    if (player?.parentTeamId) {
      team = teamIdToAbbrev[player.parentTeamId];
      teamId = player.parentTeamId;
    } else {
      // fallback in case no parentTeamId
      switch (type) {
        case PoserPlayerType.BATTER:
        case PoserPlayerType.RUNNER:
        case PoserPlayerType.COACH:
          team = battingTeam ? teamIdToAbbrev[battingTeam] : undefined;
          teamId = battingTeam;
          break;

        case PoserPlayerType.PITCHER:
        case PoserPlayerType.CATCHER:
        case PoserPlayerType.FIELDER:
          team = fieldingTeam ? teamIdToAbbrev[fieldingTeam] : undefined;
          teamId = fieldingTeam;
          break;

        default:
          team = undefined;
          teamId = undefined;
          break;
      }
    }
  }

  let outfitTemplate: OutfitMetadata | undefined = undefined;
  let materialTemplate: MaterialTemplate | undefined = undefined;

  if (team) {
    outfitTemplate = context.outfitTemplates.get(type);
    materialTemplate = context.materialsTemplates.get(team);
  }

  return {
    teamId,
    outfitTemplate: outfitTemplate,
    materialTemplate: materialTemplate,
  };
}
